import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import Icon from './Icon';

/**
 * Accessible dialog: Escape closes it, a backdrop click closes it, focus moves
 * in on open and returns to the trigger on close, and body scroll is locked
 * while it is open.
 */
export default function Modal({ open, onClose, kicker, title, children, footer, wide = false }) {
  const panelRef = useRef(null);
  const previouslyFocused = useRef(null);

  /*
   * The latest onClose, read through a ref so the effect below runs on open/close only.
   *
   * Callers pass an inline arrow, which is a new function every render. With onClose in the
   * dependency list, each keystroke in a dialog's field re-ran the effect: its cleanup sent focus
   * back to the trigger and the re-run refocused the first control — the header's ✕ button — so
   * typing in a textarea lost the cursor after one character.
   */
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return undefined;

    previouslyFocused.current = document.activeElement;

    const onKeyDown = (e) => {
      if (e.key === 'Escape') onCloseRef.current?.();
    };
    document.addEventListener('keydown', onKeyDown);

    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';

    // Focus the first control so keyboard users start inside the dialog — a field in the body
    // before the header's ✕, and never over a field that autoFocus already put the cursor in.
    const timer = setTimeout(() => {
      const panel = panelRef.current;
      if (!panel || panel.contains(document.activeElement)) return;
      const controls = 'input, select, textarea, button:not([disabled])';
      const target = panel.querySelector(`.modal-body :is(${controls})`) || panel.querySelector(controls);
      target?.focus();
    }, 40);

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = overflow;
      clearTimeout(timer);
      previouslyFocused.current?.focus?.();
    };
  }, [open]);

  if (!open) return null;

  return createPortal(
    <div
      className="modal-backdrop-custom"
      onMouseDown={(e) => {
        // Only a click that both starts and ends on the backdrop closes it, so a
        // drag that ends outside the panel does not discard the form.
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div
        className={`modal-panel ${wide ? 'modal-panel--wide' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        ref={panelRef}
      >
        <div className="modal-head" style={{ position: 'relative' }}>
          {kicker && <div className="modal-kicker">{kicker}</div>}
          <h2 className="modal-title">{title}</h2>
          <button
            type="button"
            className="btn-icon"
            onClick={onClose}
            aria-label="Close"
            style={{ position: 'absolute', top: 18, right: 18 }}
          >
            <Icon name="close" />
          </button>
        </div>

        <div className="modal-body">{children}</div>

        {footer && <div className="modal-foot">{footer}</div>}
      </div>
    </div>,
    document.body
  );
}
